/** First-party cookie consent (localStorage, ~1 year). */

export type CookieConsentChoice = "all" | "necessary";

export type CookieConsentRecord = {
  choice: CookieConsentChoice;
  updatedAt: string;
  version: 1;
};

export const COOKIE_CONSENT_STORAGE_KEY = "bilenia_cookie_consent_v1";
export const COOKIE_CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;
/** Open consent UI (initial or change settings). */
export const COOKIE_CONSENT_OPEN_EVENT = "bilenia:cookie-consent-open";

export function readCookieConsent(): CookieConsentRecord | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentRecord;
    if (
      !parsed ||
      (parsed.choice !== "all" && parsed.choice !== "necessary") ||
      typeof parsed.updatedAt !== "string"
    ) {
      return null;
    }
    const age = Date.now() - new Date(parsed.updatedAt).getTime();
    if (!Number.isFinite(age) || age < 0 || age > COOKIE_CONSENT_TTL_MS) {
      return null;
    }
    return { ...parsed, version: 1 };
  } catch {
    return null;
  }
}

export function hasValidCookieConsent(): boolean {
  return readCookieConsent() != null;
}

export function allowsMarketingCookies(): boolean {
  return readCookieConsent()?.choice === "all";
}

export function saveCookieConsent(choice: CookieConsentChoice): CookieConsentRecord {
  const record: CookieConsentRecord = {
    choice,
    updatedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private mode / quota */
  }
  window.dispatchEvent(
    new CustomEvent("bilenia:cookie-consent-changed", { detail: record }),
  );
  return record;
}

export type CookieConsentOpenReason = "settings" | "valuation_requires_marketing";

export type OpenCookieConsentDetail = {
  /** Prefill previous choice when changing settings */
  edit?: boolean;
  /** Why the prompt opened (e.g. valuation blocked without marketing cookies) */
  reason?: CookieConsentOpenReason;
};

export function openCookieConsentSettings(detail: OpenCookieConsentDetail = {}): void {
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_OPEN_EVENT, {
      detail: {
        edit: detail.edit !== false,
        reason: detail.reason ?? "settings",
      },
    }),
  );
}
